import { Capacitor } from '@capacitor/core';
import { LocalNotifications } from '@capacitor/local-notifications';
import { Preferences } from '@capacitor/preferences';
import KoreanLunarCalendar from 'korean-lunar-calendar';

import type {
  CalendarMonth,
  HealthResponse,
  HomeSummary,
  ImportantDate,
  ImportantDateOccurrence,
  ImportantDatePayload,
  ImportantDateWithOccurrence,
  Person,
  PersonPayload,
  TargetType,
} from './types';

export const LOCAL_STORE_KEY = 'daykeeper.v1.local-store';

type LocalStore = {
  schemaVersion: 1;
  people: Person[];
  dates: ImportantDate[];
  updatedAt: string;
};

const emptyStore = (): LocalStore => ({ schemaVersion: 1, people: [], dates: [], updatedAt: new Date().toISOString() });
const makeId = (prefix: string) => `${prefix}_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
const pad2 = (value: number) => String(value).padStart(2, '0');
const dateKey = (date: Date) => `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`;
const parseDateKey = (value: string) => {
  const [year, month, day] = value.split('-').map(Number);
  return new Date(year, month - 1, day);
};
const daysBetween = (from: Date, to: string) => {
  const target = parseDateKey(to);
  return Math.round((Date.UTC(target.getFullYear(), target.getMonth(), target.getDate()) -
    Date.UTC(from.getFullYear(), from.getMonth(), from.getDate())) / 86_400_000);
};
const addDays = (value: string, amount: number) => {
  const date = parseDateKey(value);
  date.setDate(date.getDate() + amount);
  return dateKey(date);
};

const readRaw = async () => {
  if (Capacitor.isNativePlatform()) return (await Preferences.get({ key: LOCAL_STORE_KEY })).value;
  return window.localStorage.getItem(LOCAL_STORE_KEY);
};

const writeRaw = async (value: string) => {
  if (Capacitor.isNativePlatform()) await Preferences.set({ key: LOCAL_STORE_KEY, value });
  else window.localStorage.setItem(LOCAL_STORE_KEY, value);
};

const load = async (): Promise<LocalStore> => {
  const raw = await readRaw();
  if (!raw) return emptyStore();
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    throw new Error('로컬 데이터가 손상되었습니다. 설정에서 데이터를 초기화하거나 지원팀에 문의하세요.');
  }
  if (!parsed || typeof parsed !== 'object' || (parsed as Partial<LocalStore>).schemaVersion !== 1) {
    throw new Error('지원하지 않는 로컬 데이터 버전입니다. 앱을 업데이트한 뒤 다시 시도하세요.');
  }
  const store = parsed as LocalStore;
  if (!Array.isArray(store.people) || !Array.isArray(store.dates)) throw new Error('로컬 데이터 형식이 올바르지 않습니다.');
  return store;
};

const save = async (store: LocalStore) => writeRaw(JSON.stringify({ ...store, updatedAt: new Date().toISOString() }));

export const exportLocalData = async () => {
  const store = await load();
  return { people: store.people, importantDates: store.dates };
};

export const clearLocalData = async () => {
  if (Capacitor.isNativePlatform()) await Preferences.remove({ key: LOCAL_STORE_KEY });
  else window.localStorage.removeItem(LOCAL_STORE_KEY);
};
const mutate = async (operation: (store: LocalStore) => LocalStore) => {
  const next = operation(await load());
  await save(next);
  return next;
};

const solarDate = (date: ImportantDate, year: number) => {
  if (date.calendarType === 'solar') {
    const candidate = new Date(year, date.originalMonth - 1, date.originalDay);
    if (candidate.getMonth() === date.originalMonth - 1 && candidate.getDate() === date.originalDay) return dateKey(candidate);
    if (date.originalMonth === 2 && date.originalDay === 29) return `${year}-02-28`;
    throw new Error(`${date.title} 날짜를 계산할 수 없습니다.`);
  }
  const calendar = new KoreanLunarCalendar();
  if (!calendar.setLunarDate(year, date.originalMonth, date.originalDay, date.isLeapMonth)) {
    throw new Error(`${date.title} 음력 날짜를 계산할 수 없습니다.`);
  }
  const solar = calendar.getSolarCalendar();
  return `${solar.year}-${pad2(solar.month)}-${pad2(solar.day)}`;
};

const occurrenceFor = (date: ImportantDate, today = new Date()): ImportantDateOccurrence => {
  const currentYear = today.getFullYear();
  let targetYear = date.repeat === 'annual' ? currentYear : (date.originalYear ?? currentYear);
  const occurrenceDate = solarDate(date, targetYear);
  let nextOccurrenceDate = occurrenceDate;
  if (date.repeat === 'annual' && daysBetween(today, occurrenceDate) < 0) {
    targetYear += 1;
    nextOccurrenceDate = solarDate(date, targetYear);
  }
  const daysLeft = daysBetween(today, nextOccurrenceDate);
  const ageYear = date.originalYear == null ? null : Math.max(0, targetYear - date.originalYear);
  const birthday = date.type === 'birthday' || date.type === 'petBirthday';
  const yearsSince = date.originalYear == null || birthday || date.type === 'dday' ? null : Math.max(0, targetYear - date.originalYear);
  return {
    importantDateId: date.id,
    targetYear,
    occurrenceDate,
    nextOccurrenceDate,
    reminderDate: date.reminderEnabled ? addDays(nextOccurrenceDate, -date.reminderOffsetDays) : null,
    daysLeft,
    isToday: daysLeft === 0,
    dDayLabel: daysLeft === 0 ? 'D-day' : daysLeft > 0 ? `D-${daysLeft}` : `D+${Math.abs(daysLeft)}`,
    ageMan: birthday ? ageYear : null,
    ageYear: birthday ? ageYear : null,
    ageCounting: birthday && ageYear != null ? ageYear + 1 : null,
    yearsSince,
  };
};

const attach = (store: LocalStore, date: ImportantDate): ImportantDateWithOccurrence => ({
  ...date,
  person: store.people.find((person) => person.id === date.personId) ?? null,
  occurrence: occurrenceFor(date),
});
const allDates = (store: LocalStore) => store.dates.map((date) => attach(store, date)).sort((a, b) =>
  a.occurrence.daysLeft - b.occurrence.daysLeft || a.title.localeCompare(b.title),
);

const notificationId = (id: string) => {
  let hash = 0;
  for (const char of id) hash = ((hash << 5) - hash + char.charCodeAt(0)) | 0;
  return Math.abs(hash) || 1;
};

const reconcileNotification = async (date: ImportantDateWithOccurrence | null, deletedId?: string) => {
  if (!Capacitor.isNativePlatform()) return;
  const id = notificationId(deletedId ?? date?.id ?? '');
  await LocalNotifications.cancel({ notifications: [{ id }] });
  if (!date?.reminderEnabled || !date.occurrence.reminderDate) return;
  const permission = await LocalNotifications.checkPermissions();
  if (permission.display !== 'granted') return;
  const scheduleAt = new Date(`${date.occurrence.reminderDate}T09:00:00`);
  if (scheduleAt.getTime() <= Date.now()) return;
  await LocalNotifications.schedule({ notifications: [{
    id,
    title: `${date.person?.name ?? 'DayKeeper'} · ${date.title}`,
    body: `${date.occurrence.dDayLabel} · ${date.occurrence.nextOccurrenceDate}`,
    schedule: { at: scheduleAt },
    extra: { personId: date.personId, importantDateId: date.id },
  }] });
};

export const localApi = {
  getHealth: async (): Promise<HealthResponse> => ({ ok: true, db: { ready: true } }),
  getPeople: async (query?: { q?: string; targetType?: TargetType }) => {
    const store = await load();
    const q = query?.q?.trim().toLowerCase();
    return store.people.filter((person) => (!query?.targetType || person.targetType === query.targetType) &&
      (!q || [person.name, person.relation, person.category, person.memo, person.tags.join(' ')].filter(Boolean).join(' ').toLowerCase().includes(q)));
  },
  getPersonById: async (id: string) => {
    const person = (await load()).people.find((entry) => entry.id === id);
    if (!person) throw new Error('대상을 찾을 수 없습니다.');
    return person;
  },
  createPerson: async (payload: PersonPayload) => {
    const now = new Date().toISOString();
    const person: Person = { id: makeId('person'), ...payload, createdAt: now, updatedAt: now };
    await mutate((store) => ({ ...store, people: [person, ...store.people] }));
    return person;
  },
  updatePerson: async (id: string, payload: Partial<PersonPayload>) => {
    let result: Person | undefined;
    await mutate((store) => ({ ...store, people: store.people.map((person) => {
      if (person.id !== id) return person;
      result = { ...person, ...payload, updatedAt: new Date().toISOString() };
      return result;
    }) }));
    if (!result) throw new Error('대상을 찾을 수 없습니다.');
    return result;
  },
  deletePerson: async (id: string) => {
    const store = await load();
    await Promise.all(store.dates.filter((date) => date.personId === id).map((date) => reconcileNotification(null, date.id)));
    await save({ ...store, people: store.people.filter((person) => person.id !== id), dates: store.dates.filter((date) => date.personId !== id) });
    return { ok: true };
  },
  getPersonDates: async (personId: string) => {
    const store = await load();
    return allDates(store).filter((date) => date.personId === personId);
  },
  createPersonDate: async (personId: string, payload: ImportantDatePayload) => {
    const now = new Date().toISOString();
    const date: ImportantDate = { id: makeId('date'), personId, ...payload, createdAt: now, updatedAt: now };
    const store = await mutate((current) => ({ ...current, dates: [date, ...current.dates] }));
    const result = attach(store, date);
    await reconcileNotification(result);
    return result;
  },
  getImportantDateById: async (id: string) => {
    const store = await load();
    const date = store.dates.find((entry) => entry.id === id);
    if (!date) throw new Error('중요 날짜를 찾을 수 없습니다.');
    return attach(store, date);
  },
  updateImportantDate: async (id: string, payload: Partial<ImportantDatePayload>) => {
    let updated: ImportantDate | undefined;
    const store = await mutate((current) => ({ ...current, dates: current.dates.map((date) => {
      if (date.id !== id) return date;
      updated = { ...date, ...payload, updatedAt: new Date().toISOString() };
      return updated;
    }) }));
    if (!updated) throw new Error('중요 날짜를 찾을 수 없습니다.');
    const result = attach(store, updated);
    await reconcileNotification(result);
    return result;
  },
  deleteImportantDate: async (id: string) => {
    await reconcileNotification(null, id);
    await mutate((store) => ({ ...store, dates: store.dates.filter((date) => date.id !== id) }));
    return { ok: true };
  },
  getHome: async (days = 60): Promise<HomeSummary> => {
    const entries = allDates(await load());
    return {
      today: entries.filter((date) => date.occurrence.daysLeft === 0),
      upcoming: entries.filter((date) => date.occurrence.daysLeft > 0 && date.occurrence.daysLeft <= days),
      meta: { days, targetYear: new Date().getFullYear(), generatedAt: new Date().toISOString() },
    };
  },
  getCalendarMonth: async (year: number, month: number): Promise<CalendarMonth> => {
    const prefix = `${year}-${pad2(month)}-`;
    const entries = allDates(await load()).filter((date) => date.occurrence.nextOccurrenceDate.startsWith(prefix));
    const groups = new Map<string, ImportantDateWithOccurrence[]>();
    entries.forEach((entry) => groups.set(entry.occurrence.nextOccurrenceDate, [...(groups.get(entry.occurrence.nextOccurrenceDate) ?? []), entry]));
    const days = [...groups.entries()].sort(([a], [b]) => a.localeCompare(b)).map(([date, items]) => ({ date, count: items.length, items }));
    return { year, month, markers: days.map(({ date, count }) => ({ date, count })), days };
  },
};

export const localRuntime = {
  enabled: Capacitor.isNativePlatform() || import.meta.env.VITE_DATA_SOURCE !== 'remote',
  permission: async () => Capacitor.isNativePlatform() ? (await LocalNotifications.checkPermissions()).display : 'unsupported',
  requestPermission: async () => Capacitor.isNativePlatform() ? (await LocalNotifications.requestPermissions()).display : 'unsupported',
};
